import {
  DEFAULT_LEADERBOARD_DIRECTION,
  DEFAULT_LEADERBOARD_METRIC,
  DEFAULT_LEADERBOARD_PERIOD,
  DEFAULT_LEADERBOARD_SEGMENT,
  LEADERBOARD_METRICS,
  LEADERBOARD_PERIODS,
  LEADERBOARD_SEGMENTS,
  LEADERBOARD_SORT_DIRECTIONS,
  type LeaderboardPeriod,
  type LeaderboardSegment,
  type LeaderboardSort,
} from '@/lib/api/rewards-leaderboard/models';

/**
 * The leaderboard's switches live in the URL
 * (`?tab=prediction&sort=roe&dir=asc&period=day&page=3`, 19.10), so Back from a player's
 * profile lands on the board it was opened from, and a refresh or a shared link reads it
 * too. Defaults are left out of the address.
 */

// ── View ──

/** The column and direction the overall ranking is sorted by, and the switches around it. */
export interface LeaderboardView extends LeaderboardSort {
  segment: LeaderboardSegment;
  /** The period switch: it counts the category winners and the overall ranking. */
  period: LeaderboardPeriod;
  /** The overall ranking's page, 1-based. */
  page: number;
}

export const DEFAULT_LEADERBOARD_VIEW: LeaderboardView = {
  segment: DEFAULT_LEADERBOARD_SEGMENT,
  metric: DEFAULT_LEADERBOARD_METRIC,
  direction: DEFAULT_LEADERBOARD_DIRECTION,
  period: DEFAULT_LEADERBOARD_PERIOD,
  page: 1,
};

const TAB_PARAM = 'tab';
const SORT_PARAM = 'sort';
const DIRECTION_PARAM = 'dir';
const PERIOD_PARAM = 'period';
const PAGE_PARAM = 'page';

// ── Read ──

/** `useSearchParams()` in the browser, the page's `searchParams` on the server. */
type SearchParamsSource =
  | { get(name: string): string | null }
  | Record<string, string | string[] | undefined>;

function readParam(params: SearchParamsSource, name: string): string | undefined {
  if (typeof params.get === 'function') return params.get(name) ?? undefined;
  const value = (params as Record<string, string | string[] | undefined>)[name];
  return Array.isArray(value) ? value[0] : value;
}

function oneOf<T extends string>(value: string | undefined, allowed: readonly T[], fallback: T): T {
  return allowed.find((item) => item === value) ?? fallback;
}

/** The view a URL asks for. A value the page has no switch for falls back to its default. */
export function leaderboardViewFromSearchParams(params: SearchParamsSource): LeaderboardView {
  const page = Number(readParam(params, PAGE_PARAM));
  return {
    segment: oneOf(readParam(params, TAB_PARAM), LEADERBOARD_SEGMENTS, DEFAULT_LEADERBOARD_SEGMENT),
    metric: oneOf(readParam(params, SORT_PARAM), LEADERBOARD_METRICS, DEFAULT_LEADERBOARD_METRIC),
    direction: oneOf(
      readParam(params, DIRECTION_PARAM),
      LEADERBOARD_SORT_DIRECTIONS,
      DEFAULT_LEADERBOARD_DIRECTION,
    ),
    period: oneOf(readParam(params, PERIOD_PARAM), LEADERBOARD_PERIODS, DEFAULT_LEADERBOARD_PERIOD),
    page: Number.isSafeInteger(page) && page > 1 ? page : 1,
  };
}

// ── Write ──

function setParam(params: URLSearchParams, name: string, value: string, fallback: string): void {
  if (value === fallback) params.delete(name);
  else params.set(name, value);
}

/** Writes `view` into `params`, deleting what is at its default; other parameters stay. */
export function writeLeaderboardView(params: URLSearchParams, view: LeaderboardView): void {
  setParam(params, TAB_PARAM, view.segment, DEFAULT_LEADERBOARD_VIEW.segment);
  setParam(params, SORT_PARAM, view.metric, DEFAULT_LEADERBOARD_VIEW.metric);
  setParam(params, DIRECTION_PARAM, view.direction, DEFAULT_LEADERBOARD_VIEW.direction);
  setParam(params, PERIOD_PARAM, view.period, DEFAULT_LEADERBOARD_VIEW.period);
  setParam(params, PAGE_PARAM, String(view.page), String(DEFAULT_LEADERBOARD_VIEW.page));
}

/**
 * Puts `view` in the address bar without a navigation (Next's `useSearchParams` follows it).
 * Replaced, not pushed: a switch on the board is not a step Back should undo.
 */
export function replaceLeaderboardViewInUrl(view: LeaderboardView): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  writeLeaderboardView(params, view);
  const query = params.toString();
  const next = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (next === current) return;
  window.history.replaceState(null, '', next);
}
